import { useState, useEffect, useRef, useMemo } from 'react';
import { doc, runTransaction, updateDoc } from 'firebase/firestore';
import { db } from "../lib/firebase.js";
import { motion, useAnimationControls } from 'motion/react';
import { CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { RadioGroup } from './ui/radio-group';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { isAgeRestricted } from '../utils/productHelpers.js';
import { Printer as _Printer } from '../utils/Printer.js';
import { ReceiptGenerator } from '../utils/ReceiptGenerator.js';
import Currency from '../utils/Currency.js';
import Lottie from "lottie-react";
import processFailedAnimation from "../assets/lottie/processFailedAnimation.json"
import processSuccessAnimation from "../assets/lottie/processSuccessAnimation.json"
import printingAnimation2 from "../assets/lottie/printingAnimation2.json";
import waitingAnimation from "../assets/lottie/waitingAnimation.json";
import { LoaderCircle } from 'lucide-react';
import blackArrow from "../images/black-arrow.svg";
import whiteArrow from "../images/white-arrow.svg";
import mdWhite from "../images/md-white.svg";
import mdBlack from "../images/md-black.svg";
import { ImageWrapper } from './ImageWrapper.jsx';
import { useTheme } from "../hooks/useTheme.jsx";
import { useCartSaleCode } from "../hooks/useCartSaleCode.js";
import { PaymentWaitScreen } from "./PaymentWaitScreen.jsx";
import { getPaymentWaitConfig } from "../utils/paymentWait.js";
import { ABORT_CLOSED, ABORT_TIMEOUT, PENDING_STATUSES, getPaymentAbortMs, waitUntil } from "../utils/waitForPayment.js";
import { getConnectivityConfig } from "../utils/connectivity.js";
import { fs } from '../utils/a11y.js';
import { classifyPaymentFailure, getFailedCartId } from '../utils/paymentFailure.js';
import { checkoutTexts } from '../i18n/checkout.es.js';

const failureTexts = checkoutTexts.paymentFailure;
const connectivityTexts = checkoutTexts.connectivity;
// El aviso de sin conexión es más largo que "Confirmar Pedido" y el cliente tiene que leerlo: va
// entero (sin la opacidad de deshabilitado), crece con "Letra grande" y el botón se alarga si pasa
// a dos o tres líneas. --color-muted / --color-on-muted ya tienen su versión de contraste alto.
const BLOCKED_BUTTON_STYLE = {
  backgroundColor: 'var(--color-muted)',
  color: 'var(--color-on-muted)',
  height: 'auto',
  minHeight: '80px',
  padding: '12px 24px',
  opacity: 1,
  cursor: 'not-allowed',
};
const BLOCKED_TEXT_STYLE = { color: 'var(--color-on-muted)', fontSize: fs(24), lineHeight: fs(30), whiteSpace: 'normal' };

// SOK-04 (punto 4): el cobro se cortó por la red y no se pudo confirmar en la espera máxima. No es un
// motivo de rechazo (no va al carro, PAYMENT_FAILURE_REASONS): el POS pudo aprobar.
const UNCONFIRMED = 'unconfirmed';

// Aviso de la pantalla de espera de pago (KIO-01).
const PAYMENT_SOUND_SRC = "sounds/clock-sound.mp3";

export function CheckoutModal({
  currency,
  locale,
  env,
  client,
  open,
  onClose,
  cartItems,
  unavailableItemIds,
  onPaymentInFlightChange,
  canStartPayment = true,
  resumePendingPayment,
  enrichedItems,
  commonTotal,
  products,
  checkoutAndWaitStatusViaAPI,
  cartId,
  cart,
  store,
  onClearCart,
  setOrderStatus,
  setLoading,
  addPaymentError,
  clearPaymentErrors,
  onPOSPaymentResult,
  clientData,
  instance,
  currentView,
  style,
  backToCatalog,
  backToHome,
  shouldStartInactivityTimer,
  stopInactivityTimer,
  remoteClear,
  logActivity,
  drawerContainer
}) {
  const { theme } = useTheme();
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [expiredCheckCount, setExpiredCheckCount] = useState(0);
  const [checkoutStatus, setCheckoutStatus] = useState(null);
  const showDeveloperLogo = style?.global?.checkout?.showDeveloperLogo || false;
  const paymentMethods = style?.global?.paymentMethods || [];
  const terminalId = instance?.terminal_id;
  const redirectionIntervalRef = useRef(null);
  const redirectionTimeLimit = 10; // Seconds.
  const [redirectionTime, setRedirectionTime] = useState(0);
  const paymentWaitConfig = useMemo(() => getPaymentWaitConfig(style), [style]);
  const paymentSoundRef = useRef(null);
  // KIO-03: corta el cobro en curso al vencer la espera + la holgura, o al cerrarse el modal.
  const paymentAbortController = useRef(null);
  // Cobro en curso. Se marca antes del primer await, así que un segundo toque no alcanza a iniciar
  // otro getCheckout mientras este no termine.
  const paymentInFlightRef = useRef(false);
  // La cuenta regresiva llegó a 0 o empezó la consulta final: se muestra "Revisando tu pago".
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  // SOK-02: motivo del último cobro fallido (utils/paymentFailure.js); elige el texto de la pantalla.
  const [failureReason, setFailureReason] = useState(null);
  // SOK-04 (punto 4): el código async del cobro lee la red desde un ref (la prop cambia entre renders).
  const canStartPaymentRef = useRef(canStartPayment);
  canStartPaymentRef.current = canStartPayment;
  const recoveryWaitMs = useMemo(() => getConnectivityConfig(style).paymentRecoveryWaitSeconds * 1000, [style]);
  // La espera terminó sin red: "Verificando tu pago…" hasta que vuelva o se acabe la espera máxima.
  const [isWaitingForNetwork, setIsWaitingForNetwork] = useState(false);
  // Cobro sin confirmar que se reanuda solo al volver la red; mientras tanto no se puede reintentar,
  // porque el POS pudo haberlo aprobado.
  const unconfirmedPaymentRef = useRef(null);
  const [isResumingPayment, setIsResumingPayment] = useState(false);
  // Las funciones del padre cambian en cada render; el efecto que reanuda las lee desde aquí.
  const resumeCallbacksRef = useRef({});
  resumeCallbacksRef.current = { resumePendingPayment, logActivity, clearPaymentErrors };

  // El audio se precarga al abrir el modal para que suene sin demora cuando empieza el cobro.
  useEffect(() => {
    if (!open || !paymentWaitConfig.paymentSound || paymentSoundRef.current) return;
    try {
      const audio = new Audio(PAYMENT_SOUND_SRC);
      audio.preload = 'auto';
      audio.load();
      paymentSoundRef.current = audio;
    } catch {
      paymentSoundRef.current = null;
    }
  }, [open, paymentWaitConfig.paymentSound]);

  useEffect(() => {
    if (!open) {
      stopRedirectionTimeout();
      shouldStartInactivityTimer(instance, currentView);
      paymentAbortController.current?.abort(ABORT_CLOSED);
    }
  }, [open]);

  useEffect(() => {
    return () => paymentAbortController.current?.abort(ABORT_CLOSED);
  }, []);

  useEffect(() => {
    if (!selectedPaymentMethod && paymentMethods && paymentMethods.length === 1) {
      const value = getRadioGroupValue(paymentMethods[0]);
      handleRadioGroupChange(paymentMethods, value, false);
    }
  }, [selectedPaymentMethod, paymentMethods]);

  // Del carro solo se toman los estados finales (pagado, cancelado...). Uno pendiente ('created',
  // 'at_terminal') nunca mueve el modal: mientras el cobro corre ya se muestra la espera, y al terminar
  // manda su resultado. Un carro del polling que volvía a 'created' (el expired de Mercado Pago antes de
  // BE-04) reiniciaba el cobro en loop, y uno pendiente de la consulta final tapaba el tiempo agotado.
  useEffect(() => {
    const status = cart?.checkout_status || null;
    if (PENDING_STATUSES.includes(status)) return;
    setCheckoutStatus(status);
  }, [cart, cart?.checkout_status]);

  // Al salir del cobro la validación se retoma sola, así que en la pantalla de reintento este flag
  // ya refleja el estado real del stock.
  const hasUnavailableItems = (unavailableItemIds?.size || 0) > 0;

  // KIO-07: si alguna línea del carro es un producto +18, el resumen muestra el aviso de edad.
  // Sin productos marcados no cambia nada.
  const hasAgeRestrictedItems = useMemo(() => {
    return (cartItems || []).some((line) => isAgeRestricted((products || []).find((p) => p.id === line.productId)));
  }, [cartItems, products]);

  // Mismo gesto que en el carro: el reintento no puede avanzar, así que al tocarlo sacude la línea
  // que explica por qué.
  const reasonShake = useAnimationControls();
  const shakeReason = () => {
    reasonShake.start({
      x: [0, -12, 12, -9, 9, -5, 5, 0],
      transition: { duration: 0.45, ease: 'easeInOut' },
    });
  };

  const validatePaymentMethod = (pm, { terminalId }) => {
    if (pm) {
      if (pm.type === 'POSAPI') {
        return terminalId ? true : false;
      }
      return true;
    } else {
      return false;
    }
  }

  const getPaymentMethodBrand = (pm) => {
    if (pm && pm.type && (pm.type === 'POSAPI' || pm.type === 'POS')) {
      return pm.type_details;
    }
    return null;
  };

  useEffect(() => {
    if (!open) {
      setExpiredCheckCount(0);
      setFailureReason(null);
      // El cobro sin confirmar sigue anotado: lo confirma la recuperación de App (punto 5).
      unconfirmedPaymentRef.current = null;
      setIsResumingPayment(false);
    }
  }, [open]);

  // SOK-04: sin red solo se bloquea iniciar un cobro (confirmar o reintentar). Un cobro que ya empezó
  // no mira la conectividad: el POS puede tener la tarjeta y el polling reintenta solo (KIO-03).
  // isCreatingOrder cubre todo el cobro, con la orden en el POS incluida.
  const isBlockedByConnectivity = !canStartPayment && !isCreatingOrder;

  // Un cobro solo se inicia aquí (Confirmar Pedido o Reintentar pago), nunca por un cambio del carro.
  const handleConfirm = async (e, retry) => {
    e.preventDefault();
    if (paymentInFlightRef.current) return;
    if (!canStartPayment || isResumingPayment) return;
    if (instance?.print_mode === 'android-bridge') _Printer.wakeUpPrinterBridge();
    logActivity('checkout-modal', `${retry ? 'retry' : 'confirm'}-button`, 'click', true, {payment_method: selectedPaymentMethod, cart_details: cart.details});
    setIsVerifyingPayment(false);
    setFailureReason(null);
    setCheckoutStatus('created');
    startPayment();
  };

  // isCreatingOrder marca exactamente la ventana del cobro (de startPayment hasta su resultado, con la
  // orden en el POS incluida), así que se eleva al padre en vez de llevar una segunda bandera en
  // paralelo. Mientras esté arriba, la validación de disponibilidad queda congelada y nada puede tocar
  // el carro con el POS a medio camino.
  useEffect(() => {
    onPaymentInFlightChange?.(isCreatingOrder);
  }, [isCreatingOrder, onPaymentInFlightChange]);

  useEffect(() => {
    return () => onPaymentInFlightChange?.(false);
  }, [onPaymentInFlightChange]);

  // SOK-02: en los cobros cloud el motivo lo escribe kiosk-backend en el carro. El tótem lo escribe solo
  // como respaldo, cuando lo tuvo que clasificar él (el backend no alcanzó a darlo), y nunca encima de
  // uno que ya esté: el del backend manda. Transbank lo guarda useShoppingCart junto al resultado.
  const savePaymentFailureReason = async (response, failure) => {
    const failedCartId = getFailedCartId(response);
    if (!failedCartId || failure.source !== 'kiosk' || selectedPaymentMethod?.type !== 'POSAPI') return;
    try {
      const reference = doc(db, `kiosk/${env}/client/${client}/carts/${failedCartId}`);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(reference);
        if (!snapshot.exists() || snapshot.get('payment_failure_reason')) return;
        transaction.update(reference, { payment_failure_reason: failure.reason });
      });
    } catch (error) {
      console.error('No se pudo guardar el motivo del cobro fallido:', error);
    }
  };

  // Solo la llama handleConfirm.
  const startPayment = async () => {
    if (paymentInFlightRef.current) return;
    paymentInFlightRef.current = true;
    setIsCreatingOrder(true);
    stopInactivityTimer();
    // KIO-03: el cobro se aborta a la espera configurada (nunca menos que el timeout del POS)
    // más la holgura. Antes de rendirse, waitForPayment hace la consulta final.
    const controller = new AbortController();
    paymentAbortController.current = controller;
    const abortTimer = setTimeout(() => controller.abort(ABORT_TIMEOUT), getPaymentAbortMs(paymentWaitConfig.paymentTimeoutSeconds));
    try {
      let response = await checkoutAndWaitStatusViaAPI(selectedPaymentMethod, enrichedItems, commonTotal, clientData, instance, products, store, {
        signal: controller.signal,
        onFinalCheck: () => setIsVerifyingPayment(true),
      });
      // SOK-04 (punto 4): la espera terminó sin red. Sin red la consulta final no pudo saber nada, así
      // que no se da por perdido: se espera la red y se confirma el mismo cobro.
      if (response?.outcome === 'timeout' && response.pendingPaymentId && resumePendingPayment && !canStartPaymentRef.current) {
        response = await resumeAfterReconnect(response);
      }
      // Se cerró el modal durante el cobro: no queda pantalla que actualizar.
      if (response?.outcome === 'aborted') return;
      if (response?.outcome === UNCONFIRMED) {
        logActivity('checkout-modal', 'pos', UNCONFIRMED, false, {response: response?.data || null, pending_payment_id: response.pendingPaymentId});
        addPaymentError({type: 'communication', message: 'Cobro sin confirmar por falta de conexión', datetime: new Date().toLocaleString()});
        unconfirmedPaymentRef.current = response.pendingPaymentId;
        setIsResumingPayment(true);
        setFailureReason(UNCONFIRMED);
        setCheckoutStatus('canceled');
        return;
      }
      if (response?.outcome === 'timeout') {
        const failure = classifyPaymentFailure(response);
        logActivity('checkout-modal', 'pos', 'timeout', true, {response: response?.data || null, reason: failure.reason, reason_source: failure.source});
        addPaymentError({type: 'payment', message: 'Tiempo de espera del pago agotado', datetime: new Date().toLocaleString()});
        setFailureReason(failure.reason);
        savePaymentFailureReason(response, failure);
        setCheckoutStatus('expired');
        return;
      }
      const failure = !response || !response.status ? classifyPaymentFailure(response) : null;
      // En activity va el motivo y solo el código y mensaje del POS (failure.raw), nunca datos de tarjeta.
      logActivity('checkout-modal', 'pos', failure ? 'failed' : 'paid', true, {response: response?.data || null, ...(failure ? {reason: failure.reason, reason_source: failure.source, failure: failure.raw} : {})});
      if (response && response.status) clearPaymentErrors();
      // SOK-33: un pago aprobado confirma que el POS responde.
      if (response && response.status) onPOSPaymentResult?.({ provider: getPaymentMethodBrand(selectedPaymentMethod), paid: true });
      if (!response || !response.status) {
        if (response && !response.status) {
          let communicationError = null;
          let paymentError = null;
          if (typeof response.data === 'string') {
            try {
              const parsed = JSON.parse(response.data);
              communicationError = parsed?.message || null;
            } catch (error) {
              communicationError = null;
            }
          } else {
            communicationError = response?.data?.error || null;
            paymentError = response?.data?.responseMessage || null;
            if (!paymentError) paymentError = response?.data?.payment_summary?.response?.responseMessage || null;
          }
          if (communicationError) addPaymentError({type: 'communication', message: communicationError, datetime: new Date().toLocaleString()});
          // SOK-33: sólo un fallo de comunicación marca el POS caído; un rechazo no informa nada.
          if (communicationError) onPOSPaymentResult?.({ provider: getPaymentMethodBrand(selectedPaymentMethod), paid: false, communicationError });
          if (paymentError) addPaymentError({type: 'payment', message: paymentError, datetime: new Date().toLocaleString()});
        }
        setFailureReason(failure.reason);
        savePaymentFailureReason(response, failure);
        setCheckoutStatus('canceled');
      }
    } catch (error) {
      console.error('Error en checkout:', error);
      const failure = classifyPaymentFailure({status: false, data: {error: error?.message || String(error)}});
      logActivity('checkout-modal', 'pos', 'failed', true, {reason: failure.reason, reason_source: failure.source, failure: failure.raw});
      setFailureReason(failure.reason);
      setCheckoutStatus('canceled');
    } finally {
      clearTimeout(abortTimer);
      if (paymentAbortController.current === controller) paymentAbortController.current = null;
      paymentInFlightRef.current = false;
      setIsCreatingOrder(false);
      setIsVerifyingPayment(false);
    }
  };

  // SOK-04 (punto 4): espera la red (hasta recoveryWaitMs) y reanuda el mismo cobro con
  // resumePendingPayment, que nunca cobra de nuevo. Si la red vuelve y se vuelve a caer antes de
  // saber el resultado, sigue esperando. Cerrar el modal la corta.
  const resumeAfterReconnect = async (response) => {
    const controller = new AbortController();
    paymentAbortController.current = controller;
    const waitTimer = setTimeout(() => controller.abort(ABORT_TIMEOUT), recoveryWaitMs);
    const pendingPaymentId = response.pendingPaymentId;
    logActivity('checkout-modal', 'pos', 'waiting-network', false, {pending_payment_id: pendingPaymentId});
    setIsVerifyingPayment(true);
    setIsWaitingForNetwork(true);
    try {
      for (;;) {
        const online = await waitUntil(() => canStartPaymentRef.current, { signal: controller.signal });
        if (!online) {
          if (controller.signal.reason === ABORT_CLOSED) return { status: false, outcome: 'aborted', data: null };
          return { status: false, outcome: UNCONFIRMED, data: response.data, pendingPaymentId };
        }
        setIsWaitingForNetwork(false);
        const resumed = await resumePendingPayment(pendingPaymentId);
        if (resumed.outcome !== 'timeout' || canStartPaymentRef.current) return resumed;
        setIsWaitingForNetwork(true);
      }
    } finally {
      clearTimeout(waitTimer);
      if (paymentAbortController.current === controller) paymentAbortController.current = null;
      setIsWaitingForNetwork(false);
    }
  };

  // SOK-04 (punto 4): el cobro quedó sin confirmar; al volver la red se confirma solo. Pagado: el carro
  // se carga y el modal pasa a imprimir. Rechazado: el fallo de siempre. Sigue pendiente: el aviso de
  // tiempo agotado (si el POS aún muestra el cobro, cancelarlo ahí) y recién ahí se puede reintentar.
  useEffect(() => {
    const pendingPaymentId = unconfirmedPaymentRef.current;
    const { resumePendingPayment, logActivity, clearPaymentErrors } = resumeCallbacksRef.current;
    if (!open || !canStartPayment || !pendingPaymentId || !resumePendingPayment) return;
    unconfirmedPaymentRef.current = null;
    resumePendingPayment(pendingPaymentId)
      .then((result) => {
        logActivity('checkout-modal', 'pos', `resumed-${result.outcome}`, false, {pending_payment_id: pendingPaymentId});
        if (result.outcome === 'paid') {
          clearPaymentErrors();
          setFailureReason(null);
        } else if (result.outcome === 'canceled') {
          setFailureReason(classifyPaymentFailure(result).reason);
        } else {
          setFailureReason(null);
          setCheckoutStatus('expired');
        }
      })
      .catch((error) => {
        console.error('No se pudo confirmar el cobro reanudado:', error);
        setFailureReason(null);
        setCheckoutStatus('expired');
      })
      .finally(() => setIsResumingPayment(false));
  }, [open, canStartPayment, failureReason]);

  const handleRadioGroupChange = (paymentMethods, value, log) => {
    if (value) {
      const _paymentMethod = paymentMethods.find(pm => getRadioGroupValue(pm) === value);
      if (log) logActivity('checkout-modal', 'payment-method', 'click', true, {payment_method: _paymentMethod, cart_details: cart.details});
      setSelectedPaymentMethod(_paymentMethod);
    } else {
      setSelectedPaymentMethod(null);
    }
  };

  const getRadioGroupValue = (pm) => {
    return pm ? [pm.type, pm.type_details].join('-') : 'none';
  };

  useEffect(() => {
    const _setPrintWaitMode = async () => {
      let receiptId = null;
      let receiptCode = null;
      let receiptContent = null;

      try {
        const generationResponse = await ReceiptGenerator.generateReceipt(clientData, cart);
        if (generationResponse) {
          receiptId = generationResponse.id;
          receiptCode = generationResponse.code;
        }
      } catch (error) {
        console.error('Receipt generation error:', error);
      }

      try {
        const consolidationResponse = await ReceiptGenerator.consolidateReceipt(receiptId);
        if (consolidationResponse) {
          receiptContent = consolidationResponse.code;
        }
      } catch (error) {
        console.error('Receipt consolidation error:', error);
      }

      const data = {ready_to_print: true};
      if (receiptId) data["ticket_data.security_id"] = receiptId;
      if (receiptCode) data["ticket_data.security_code"] = receiptCode;
      if (receiptContent) data["ticket_data.security_content"] = receiptContent;

      const reference = doc(db, `kiosk/${env}/client/${client}/carts/${cartId}`);
      try {
        await updateDoc(reference, data);
      } catch (error) {
        console.error('Error updating cart with receipt data:', error);
      }
      setTimeout(() => {
        logActivity('checkout-modal', 'printer', 'print', false, {payment_method: selectedPaymentMethod, cart_details: cart.details});
        setCheckoutStatus('printed');
      }, 5000);
    }
    if (checkoutStatus === 'paid' || checkoutStatus === 'processed') _setPrintWaitMode();
  }, [checkoutStatus]);

  useEffect(() => {
    if (checkoutStatus === 'printed') startRedirectionTimeout();
  }, [checkoutStatus]);

  useEffect(() => {
    if (
      checkoutStatus !== 'created'     &&
      checkoutStatus !== 'at_terminal' &&
      checkoutStatus !== 'paid'        &&
      checkoutStatus !== 'processed'
    ) shouldStartInactivityTimer(instance, currentView);
  }, [checkoutStatus]);

  const startRedirectionTimeout = () => {
    stopRedirectionTimeout();
    redirectionIntervalRef.current = setInterval(async () => {
      setRedirectionTime(prev => prev + 1);
    }, 1000);
  };

  const stopRedirectionTimeout = () => {
    if (redirectionIntervalRef.current) {
      clearInterval(redirectionIntervalRef.current);
      redirectionIntervalRef.current = null;
      setRedirectionTime(0);
    }
  };

  useEffect(() => {
    if (redirectionTime >= redirectionTimeLimit) {
      logActivity('checkout-modal', 'printer', 'redirect', false, {payment_method: selectedPaymentMethod, cart_details: cart.details});
      stopRedirectionTimeout();
      backToHome();
      handleClear();
    }
  }, [redirectionTime]);

  const handleCancel = () => {
    if (!["at_terminal", "created", "creating", "paid", "processed"].includes(statusMessage)) {
      if (statusMessage === "printed" || statusMessage === "expired" || statusMessage === "canceled") {
        onClearCart();
      } else {
        setOrderStatus(null);
        setLoading(false);
        setCheckoutStatus(null);
      }
      logActivity('checkout-modal', 'cancel-button/close-button/overlay', 'click', true, {cart_details: cart.details});
      onClose();
    }
  };

  const handleClear = async () => {
    setCheckoutStatus(null);
    onClose();
    await onClearCart();
  };

  useEffect(() => {
    if (remoteClear) handleClear();
  }, [remoteClear]);

  const getStatusMessage = () => {
    // Si no hay estado o el estado no existe, mostrar resumen normal
    if (!checkoutStatus) {
      return null; // Mostrar resumen normal
    }

    // Si el estado es canceled o expired (después de 2 consultas)
    if (checkoutStatus === 'canceled' || (checkoutStatus === 'expired' && expiredCheckCount >= 0)) {
      return 'canceled';
    }

    // Si el estado es created
    if (checkoutStatus === 'created') {
      return 'creating';
    }

    // Si el estado es at_terminal
    if (checkoutStatus === 'at_terminal') {
      return 'at_terminal';
    }

    // Si el estado es paid o processed
    if (checkoutStatus === 'paid' || checkoutStatus === 'processed') {
      return 'paid';
    }

    // Si el estado es expired pero aún no se han hecho 3 consultas
    if (checkoutStatus === 'expired') {
      return 'expired'; // Seguir mostrando la espera en terminal hasta que se cumplan las 3 consultas
    }

    if (checkoutStatus === 'printed') {
      return 'printed'; // Seguir mostrando la espera en terminal hasta que se cumplan las 3 consultas
    }

    return null; // Estado desconocido, mostrar resumen normal
  };

  const statusMessage = getStatusMessage();

  // KIO-11 / BE-18: número de pedido (carts.sale_code) en la pantalla de confirmado. Lo genera un
  // trigger, así que puede llegar después del pago; se escucha el carro y se muestra cuando aparezca.
  // Si a los 3 s no llegó, se muestra external_order_id (o numeric_id) para no dejar el espacio vacío.
  const isConfirmedView = statusMessage === 'paid' || statusMessage === 'printed';
  const liveCart = useCartSaleCode(env, client, cartId, isConfirmedView);
  const fallbackId = liveCart.externalOrderId || cart?.external_order_id || liveCart.numericId || cart?.numeric_id || null;
  const saleCode = liveCart.saleCode || cart?.sale_code || (liveCart.timedOut ? fallbackId : null);

  // El espacio queda reservado desde el inicio (alto fijo) para que el código no empuje el contenido
  // ni parpadee al llegar: solo cambia la opacidad.
  const renderSaleCode = () => (
    <div className="flex items-center justify-center mb-4" style={{ height: '48px' }} aria-live="polite">
      <motion.span
        className="main-font-bold"
        style={{ fontSize: fs(36), lineHeight: fs(48) }}
        initial={false}
        animate={{ opacity: saleCode ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {saleCode ? `Tu pedido ${saleCode}` : '\u00A0'}
      </motion.span>
    </div>
  );

  const renderContent = () => {
    const isPaying = statusMessage === 'creating' || statusMessage === 'at_terminal' || isCreatingOrder;
    if (isPaying && isVerifyingPayment) {
      // KIO-03: la cuenta regresiva terminó, pero el cobro sigue abierto hasta la holgura y la
      // consulta final; un pago que llegue tarde todavía se acepta.
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="a11y-checkout-anim w-100 h-100 my-0 mx-auto">
              <Lottie
                animationData={waitingAnimation}
                loop={true}
                autoplay={true}
              />
            </div>
            <h2 className="main-font-bold mb-4" style={{ fontSize: fs(24), lineHeight: fs(32) }}>
              {isWaitingForNetwork ? connectivityTexts.waitingTitle : 'Revisando tu pago'}
            </h2>
            <p className="secondary-font-regular" role={isWaitingForNetwork ? 'status' : undefined} style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>
              {isWaitingForNetwork ? connectivityTexts.waitingMessage : 'Por favor espera un momento...'}
            </p>
          </div>
        </div>
      );
    }
    if (isPaying) {
      // Mismo componente en creating y at_terminal: no se vuelve a montar al cambiar de estado, así
      // que la cuenta regresiva y el sonido de inicio corren una sola vez por intento de cobro.
      // Al llegar a 0 se pasa a "Revisando tu pago"; el corte real lo hace el abort (KIO-03).
      return (
        <PaymentWaitScreen
          amount={new Currency(currency, locale).format(commonTotal)}
          brand={getPaymentMethodBrand(selectedPaymentMethod)}
          config={paymentWaitConfig}
          audioRef={paymentSoundRef}
          theme={theme}
          logActivity={logActivity}
          activityMetadata={{payment_method: selectedPaymentMethod}}
          onTimeout={() => setIsVerifyingPayment(true)}
        />
      );
    } else if (statusMessage === 'expired') {
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="a11y-checkout-anim w-100 h-100 my-0 mx-auto">
              <Lottie
                animationData={waitingAnimation}
                loop={false}
                autoplay={true}
              />
            </div>
            <h2 className="main-font-bold mb-4" style={{ fontSize: fs(24), lineHeight: fs(32) }}>
              🟡 Revisando orden
            </h2>
            <p className="secondary-font-regular" style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>
              Por favor espera un momento...
            </p>
          </div>
        </div>
      );
    } else if (statusMessage === 'canceled') {
      // SOK-02: el aviso de stock manda sobre el motivo del pago, porque el reintento no puede avanzar.
      // SOK-04: sin confirmar va antes que el stock: el POS pudo haber cobrado.
      const failureText = failureReason === UNCONFIRMED ? connectivityTexts.unconfirmed :
        hasUnavailableItems ? failureTexts.outOfStock :
        checkoutStatus === 'expired' ? failureTexts.expired :
          (failureTexts.reasons[failureReason] || failureTexts.reasons.unknown);
      // Mientras se confirma el cobro cortado no se reintenta; sin red lo dice el aviso de conexión.
      const retryBlocked = isBlockedByConnectivity || isResumingPayment;
      const retryBlockedText = isBlockedByConnectivity ? connectivityTexts.blockedButton : connectivityTexts.resumingButton;
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="a11y-checkout-anim w-100 h-100 my-0 mx-auto">
              {/* SOK-04: sin confirmar no es un fallo (el POS pudo cobrar): reloj de arena, no la X. */}
              <Lottie
                animationData={failureReason === UNCONFIRMED ? waitingAnimation : processFailedAnimation}
                loop={failureReason === UNCONFIRMED}
                autoplay={true}
              />
            </div>
            <h2 className="main-font-bold mb-4" style={{ fontSize: fs(24), lineHeight: fs(32) }}>
              {failureText.title}
            </h2>
            <motion.p
              role="alert"
              animate={hasUnavailableItems ? reasonShake : undefined}
              className="secondary-font-regular mb-6"
              style={{ color: hasUnavailableItems ? '#D92D20' : 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}
            >
              {failureText.message}
            </motion.p>
            <div className="space-y-3 pt-4">
              <div className="space-y-8">
                {/* El motivo va en la línea de arriba, que ya existía: un bloque extra sobre el botón
                    empujaba "Nueva compra" fuera de la vista, porque este modal tiene alto fijo. */}
                {hasUnavailableItems && failureReason !== UNCONFIRMED ? (
                  <motion.div
                    onClick={shakeReason}
                    className="w-full btn btn-primary text-center opacity-50 flex items-center justify-center cursor-pointer"
                    style={{height: '80px'}}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span className='text-3xl'>{failureTexts.retryButton}</span>
                  </motion.div>
                ) : (
                <motion.button
                  type="button"
                  onClick={(e) => handleConfirm(e, true)}
                  disabled={isCreatingOrder || retryBlocked}
                  className="w-full btn btn-primary text-center cursor-pointer"
                  style={{
                    height: '80px',
                    fontSize: fs(18),
                    lineHeight: fs(27),
                    ...(retryBlocked ? BLOCKED_BUTTON_STYLE : {})
                  }}
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.03 }}
                  animate={ isCreatingOrder || retryBlocked ? {boxShadow: '0px 0px 0px 0px #000000'} :
                    isCreatingOrder ? 
                    {boxShadow: ['0px 0px 30px 0px #00c950', '0px 0px 30px 15px #00c950', '0px 0px 30px 0px #00c950']} : 
                    {boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)']}
                  }
                  transition={ isCreatingOrder || retryBlocked ? {} : { boxShadow: { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' } }}
                >
                  <span className={retryBlocked ? '' : 'text-3xl'} style={retryBlocked ? BLOCKED_TEXT_STYLE : undefined}>
                    {isCreatingOrder ? failureTexts.processingButton : retryBlocked ? retryBlockedText : failureTexts.retryButton}
                  </span>
                </motion.button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  logActivity('checkout-modal', 'new-purchase-button', 'click', true, {payment_method: selectedPaymentMethod, cart_details: cart.details});
                  handleClear();
                  backToCatalog();
                }}
                disabled={isCreatingOrder}
                className="cursor-pointer w-full text-center secondary-font-bold"
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: fs(16),
                  lineHeight: fs(24),
                  padding: '12px 0'
                }}
              >
                {failureTexts.newPurchaseButton}
              </button>
            </div>
          </div>
        </div>
      );
    } else if (statusMessage === 'paid') {
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="a11y-checkout-anim w-150 h-150 my-0 mx-auto">
              <Lottie
                animationData={processSuccessAnimation}
                loop={false}
                autoplay={true}
              />
            </div>
            <h2 className="main-font-bold mb-4" style={{ fontSize: fs(24), lineHeight: fs(32) }}>
              Gracias por tu compra
            </h2>
            {renderSaleCode()}
            <p className="secondary-font-regular mb-6" style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>
              Tu pedido ha sido procesado exitosamente
            </p>
          </div>
        </div>
      );
    } else if (statusMessage === 'printed') {
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="a11y-checkout-anim w-100 h-100 my-0 mx-auto">
              <Lottie animationData={printingAnimation2} loop={true} autoplay={true}/>
            </div>
            <h2 className="main-font-bold mb-4" style={{ fontSize: fs(24), lineHeight: fs(32) }}>Imprimiendo tus tickets</h2>
            {renderSaleCode()}
            <p className="secondary-font-regular mb-6" style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>Por favor no tires hasta que salga el papel</p>
            <div className="space-y-3 pt-4">
              <div className="space-y-8">
                <motion.button
                  type="button"
                  onClick={async () => {
                    logActivity('checkout-modal', 'catalog-button', 'click', true, {payment_method: selectedPaymentMethod, cart_details: cart.details});
                    stopRedirectionTimeout();
                    backToCatalog();
                    handleClear();
                  }}
                  disabled={false}
                  className="cursor-pointer w-full btn text-center main-font-bold"
                  style={{height: '80px', fontSize: fs(18), lineHeight: fs(27), maxWidth: '400px', margin: '0 auto'}}
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.03 }}
                >
                  <span className='text-3xl'>Ir a comprar</span>
                </motion.button>
              </div>
            </div>
            <p className="secondary-font-regular mt-6 mb-6" style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>Volviendo al inicio en {redirectionTimeLimit - redirectionTime} segundo(s)</p>
            <motion.div
              className="w-[100px] h-[100px] mx-auto"
              style={{ rotate: 90 }}
              animate={{ y: [0, 30, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ImageWrapper
                image={theme === 'dark' ? whiteArrow : blackArrow}
                alt="Look right"
                display="block"
                width="100%"
                height="100%"
                backgroundColor="transparent"
                margin="0"
                padding="0"
                borderRadius="0"
                transition={true}
                loadDelay={0.1}
                onLoad={undefined}
              />
            </motion.div>
            {showDeveloperLogo ? (
              <div className="secondary-font-regular mt-12 flex justify-center items-center gap-3" style={{ color: 'var(--color-text-muted)', fontSize: fs(16), lineHeight: fs(24) }}>
                <span>Powered By</span>
                <ImageWrapper
                  image={theme === 'dark' ? mdWhite : mdBlack}
                  alt="Motion Displays"
                  display="block"
                  width="100px"
                  height="auto"
                  backgroundColor="transparent"
                  margin="0"
                  padding="0"
                  borderRadius="0"
                  transition={true}
                  loadDelay={0.1}
                  onLoad={undefined}
                />
              </div>
            ) : null}
          </div>
        </div>
      );
    } else {
      return renderOrderSummary();
    }
  };

  const renderOrderSummary = () => {
    let disabledConfirm = isCreatingOrder ? true            : (!selectedPaymentMethod ? true                         : (validatePaymentMethod(selectedPaymentMethod, { terminalId }) ? false              : true                    ));
    let confirmText =     isCreatingOrder ? 'Procesando...' : (!selectedPaymentMethod ? 'Seleccionar método de pago' : (validatePaymentMethod(selectedPaymentMethod, { terminalId }) ? 'Confirmar Pedido' : 'Terminal no encontrada'));
    // SOK-04: el carro se conserva; el botón vuelve solo cuando vuelve la red.
    const blockedConfirm = isBlockedByConnectivity && !disabledConfirm;
    if (blockedConfirm) {
      disabledConfirm = true;
      confirmText = connectivityTexts.blockedButton;
    }
    return (
      <div className={paymentMethods && paymentMethods.length > 0 ? "space-y-8" : ""}>
        {hasAgeRestrictedItems ? renderAgeNotice() : null}
        <div className="bg-back rounded-xl p-6 shadow-sm text-detail">
          <div className="space-y-3">
            <div className="flex justify-between items-center px-0 py-2">
              <span className="secondary-font-bold" style={{ fontSize: fs(22), lineHeight: fs(30) }}>
                Total
              </span>
              <span className="secondary-font-bold" style={{ fontSize: fs(22), lineHeight: fs(30) }}>
                {new Currency(currency, locale).format(commonTotal)}
              </span>
            </div>
          </div>
          {!paymentMethods || paymentMethods.length === 0 ? (
            <div className="secondary-font-regular fs-18 text-[#CD5C5C]">No se han encontrado medios de pago</div>
          ) : (
            <>
              <RadioGroup value={getRadioGroupValue(selectedPaymentMethod)} onValueChange={(value) => handleRadioGroupChange(paymentMethods, value, true)} className="space-y-0 pt-4 mt-4 border-t border-[#EAEAEA]">
                {(paymentMethods || []).map((pm) => {
                  const value = getRadioGroupValue(pm)
                    return (
                      <RadioGroupPrimitive.Item key={`radio-${value}`} value={value}>
                        <div className="flex items-center gap-4 py-1 my-1">
                          <div className="btn-primary rounded-lg flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                            <CreditCard size={28} />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="secondary-font-bold" style={{ fontSize: fs(20), lineHeight: fs(24) }}>{pm.name}</div>
                            <div className="secondary-font-bold mt-[5px]" style={{ color: 'var(--color-text-muted)', fontSize: fs(18), lineHeight: fs(20) }}>{pm.description} {pm.type_details ? ` (${pm.type_details})` : ''}</div>
                          </div>
                          <motion.div
                            className="cursor-pointer bg-mid relative w-[32px] h-[32px] rounded-full border-2 bg-back outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            whileTap={{ scale: 0.8 }}
                            whileHover={{ scale: 1.1 }}
                          >
                            <RadioGroupPrimitive.Indicator className="absolute inset-0 flex items-center justify-center">
                              <div className="w-[18px] h-[18px] rounded-full" style={{backgroundColor: 'var(--color-text)'}} />
                            </RadioGroupPrimitive.Indicator>
                          </motion.div>
                        </div>
                      </RadioGroupPrimitive.Item>
                    )
                  })
                }
              </RadioGroup>
            </>
          )}
        </div>
        <div className="space-y-3 pt-4" >
          {paymentMethods && paymentMethods.length > 0 ? (
            <motion.button
              type="button"
              onClick={(e) => handleConfirm(e, false)}
              disabled={disabledConfirm}
              className='w-full btn main-font-regular border-0! text-center text-[#FAFAFA]'
              style={{
                backgroundColor: disabledConfirm ? 'var(--color-text-muted)' : 'var(--checkout-confirm-bg)',
                color: disabledConfirm ? '#cbd5e1' : 'var(--checkout-confirm-color)',
                height: '80px',
                fontSize: fs(18),
                lineHeight: fs(27),
                opacity: disabledConfirm ? 0.5 : 1,
                cursor: disabledConfirm ? 'not-allowed' : 'pointer',
                ...(blockedConfirm ? BLOCKED_BUTTON_STYLE : {})
              }}
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.03 }}
              animate={ disabledConfirm ? {boxShadow: '0px 0px 0px 0px #000000'} : { boxShadow: ['0px 0px 30px 0px var(--checkout-confirm-glow)', '0px 0px 30px 15px var(--checkout-confirm-glow)', '0px 0px 30px 0px var(--checkout-confirm-glow)'] }}
              transition={ disabledConfirm ? {} : { boxShadow: { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' } }}
            >
              <span className={`text-[#FAFAFA] flex justify-center items-center gap-2 ${blockedConfirm ? '' : 'text-3xl'}`} style={blockedConfirm ? BLOCKED_TEXT_STYLE : undefined}>
                <span className=''>{confirmText}</span>
              </span>
            </motion.button>
          ) : null}
          <button
            type="button"
            onClick={handleCancel}
            disabled={isCreatingOrder}
            className="cursor-pointer w-full text-center secondary-font-bold"
            style={{
              color: 'var(--color-text-muted)',
              fontSize: fs(16),
              lineHeight: fs(24),
              padding: '12px 0'
            }}
          >
            Cancelar
          </button >
        </div >
      </div>
    );
  };

  // Colores fijos, no del tema: amarillo y negro de cono de seguridad, igual en claro y oscuro (contraste 14:1).
  const renderAgeNotice = () => (
    <motion.div
      role="alert"
      className="flex items-center gap-4 rounded-xl px-5 py-4 text-left"
      style={{ backgroundColor: '#FACC15', color: '#000000' }}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: [0.95, 1.03, 1], opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div
        className="main-font-bold flex items-center justify-center flex-shrink-0 rounded-full"
        style={{ width: '60px', height: '60px', backgroundColor: '#000000', color: '#FACC15', fontSize: fs(20), lineHeight: fs(20) }}
      >
        +18
      </div>
      <div className="main-font-bold" style={{ fontSize: fs(22), lineHeight: fs(28) }}>
        Debes ser mayor de 18 años
      </div>
    </motion.div>
  );

  const dialogStyle = () => {
    const smallscreenModeShort    = "w-full max-w-[927px] h-[530px] max-h-[calc(var(--app-h)*0.9)] sm:max-w-lg rounded-[24px] border-[4px]";
    const smallscreenModeAgeCheck = "w-full max-w-[927px] h-[660px] max-h-[calc(var(--app-h)*0.9)] sm:max-w-lg rounded-[24px] border-[4px]";
    const smallscreenModeCanceled = "w-full max-w-[927px] h-[860px] max-h-[calc(var(--app-h)*0.9)] sm:max-w-lg rounded-[24px] border-[4px]";
    const smallscreenModeExpired  = "w-full max-w-[927px] h-[650px] max-h-[calc(var(--app-h)*0.9)] sm:max-w-lg rounded-[24px] border-[4px]";
    const fullscreenMode          = "w-screen max-w-screen h-(--app-h) max-h-(--app-h) rounded-none border-0";
    if (statusMessage === 'creating') return fullscreenMode;
    if (statusMessage === 'at_terminal') return fullscreenMode;
    if (statusMessage === 'paid') return fullscreenMode;
    if (statusMessage === 'printed') return fullscreenMode;
    if (statusMessage === 'canceled') return smallscreenModeCanceled;
    if (statusMessage === 'expired') return smallscreenModeExpired;
    if (hasAgeRestrictedItems) return smallscreenModeAgeCheck;
    return smallscreenModeShort;
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent
        aria-describedby="Checkout Modal"
        description="Checkout Modal"
        container={drawerContainer}
        className={`bg-mid shadow-2xl overflow-hidden p-0 transition-all duration-300 ease-in-out flex flex-col justify-center items-center ${dialogStyle()}`}
      >
        <div className="w-full overflow-x-hidden overflow-y-auto px-10 py-8">
          {/* DialogTitle already renders the heading element, so the styles go on it
              directly - nesting one inside it is invalid HTML. */}
          <DialogTitle className="p-8 w-full text-center secondary-font-bold" style={{ fontSize: fs(30), lineHeight: fs(30)}}>
            {statusMessage === 'paid' ? 'Pedido Completado' :
              statusMessage === 'canceled' ? (failureReason === UNCONFIRMED ? connectivityTexts.unconfirmedDialogTitle : checkoutStatus === 'expired' || failureReason === 'timeout' ? failureTexts.timeoutDialogTitle : failureTexts.dialogTitle) :
                statusMessage ? 'Estado del Pedido' : 'Finalizar Pedido'}
          </DialogTitle>
          <div className="w-full">
            {renderContent()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}